import React, { Component } from 'react';

import { FormattedMessage, injectIntl, InjectedIntl } from 'react-intl';

import CustomThemeButton from '@atlaskit/button/custom-theme-button';
// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { Field } from '@atlaskit/form';
import ModalDialog, {
  ModalTitle,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@atlaskit/modal-dialog';
import SectionMessage from '@atlaskit/section-message';
import Spinner from '@atlaskit/spinner';
import TextArea from '@atlaskit/textarea';

import { commonMessages } from 'src/i18n';
import { PullRequestIRAnalyticsAttributes } from 'src/redux/pull-request/selectors';
import { publishUiEvent } from 'src/utils/analytics/publish';

import { messages } from '../../components/header-actions/header-actions.i18n';
import {
  DiffStateContext,
  SelectedDiffType,
} from '../tabs/diff/diff-state-context';

import * as styles from './decline-dialog.style';

type DeclineDialogProps = {
  isErrored: boolean;
  isRequesting: boolean;
  errorMessage: string | null | undefined;
  onClose: () => void;
  declinePullRequest: (reason: string) => void;
  repositoryUuid: string;
  workspaceUuid: string;
  intl: InjectedIntl;
  irTracking: PullRequestIRAnalyticsAttributes;
};

type DeclineDialogState = {
  reason: string;
};
class DeclineDialog extends Component<DeclineDialogProps, DeclineDialogState> {
  context!: React.ContextType<typeof DiffStateContext>;

  state = {
    reason: '',
  };

  static contextType = DiffStateContext;

  submit = () => {
    const { iterativeReviewSelectedDiff } = this.context;
    const irTracking = { ...this.props.irTracking };
    irTracking.irSelectedDiffType =
      SelectedDiffType[iterativeReviewSelectedDiff.selectedDiffType];

    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'declineButton',
      source: 'pullRequestScreen',
      objectType: 'repository',
      objectId: this.props.repositoryUuid,
      containerType: 'workspace',
      containerId: this.props.workspaceUuid,
      // IR tracking attributes for event: `button clicked declineButton`
      attributes: {
        irTracking,
      },
    });
    this.props.declinePullRequest(this.state.reason);
  };

  // @ts-ignore TODO: fix noImplicitAny error here
  updateReason = event => {
    this.setState({ reason: event.target.value });
  };

  render() {
    const { intl, onClose, isRequesting, isErrored, errorMessage } = this.props;

    const Footer = () => (
      <>
        <styles.DeclineButton
          appearance="danger"
          isDisabled={isRequesting || isErrored}
          onClick={this.submit}
        >
          <styles.ButtonContent>
            {isRequesting ? (
              <styles.DeclineSpinner>
                <Spinner testId="decline-dialog-footer-spinner" size="small" />
              </styles.DeclineSpinner>
            ) : null}
            <styles.InnerButtonText isDisabled={isRequesting}>
              <FormattedMessage {...messages.declinePullRequestAction} />
            </styles.InnerButtonText>
          </styles.ButtonContent>
        </styles.DeclineButton>
        <CustomThemeButton onClick={onClose}>
          <FormattedMessage {...commonMessages.cancel} />
        </CustomThemeButton>
      </>
    );

    return (
      <ModalDialog onClose={onClose} width="medium">
        <ModalHeader>
          <ModalTitle>
            <FormattedMessage {...messages.declineDialogTitle} />
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          {isErrored ? (
            <>
              <styles.DeclineErrorMessage>
                <SectionMessage
                  appearance="warning"
                  title={intl.formatMessage(messages.declineErrorInfo)}
                >
                  <p>
                    {errorMessage ||
                      intl.formatMessage(messages.declineErrorMessage)}
                  </p>
                </SectionMessage>
              </styles.DeclineErrorMessage>
            </>
          ) : null}
          <FormattedMessage {...messages.declineDialogInfo} />
          <Field
            label={intl.formatMessage(messages.declineDialogLabel)}
            name="decline-merge-dialog-textfield"
          >
            {({ fieldProps }: any) => (
              <TextArea
                autoFocus
                {...fieldProps}
                shouldFitContainer
                enableResize
                onChange={this.updateReason}
                isReadOnly={isRequesting || isErrored}
              />
            )}
          </Field>
        </ModalBody>
        <ModalFooter>
          <Footer />
        </ModalFooter>
      </ModalDialog>
    );
  }
}

export default injectIntl(DeclineDialog);
